var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { FormControl, Stack, styled, Typography, } from '@mui/material';
import clsx from 'clsx';
import * as React from 'react';
import { Labeled, LinearProgress, RecordContextProvider, ResourceContextProvider, SourceContextProvider, useResourceContext, useTranslate, } from 'react-admin';
import { useReferenceOneInputController, } from './useReferenceOneInputController';
/**
 * Use `<ReferenceOneInput>` in an `<Edit>` or `<Create>` view to edit one-to-one relationships,
 * e.g. to edit the details of a book in the book edition view.
 *
 * @example
 * import {
 *     Edit,
 *     SimpleForm,
 *     TextInput,
 *     NumberInput,
 * } from 'react-admin';
 * import { ReferenceOneInput } from '@react-admin/ra-relationships';
 *
 * const BookEdit = () => (
 *     <Edit mutationMode="optimistic">
 *         <SimpleForm>
 *             <TextInput source="title" />
 *             <ReferenceOneInput reference="book_details" target="book_id">
 *                 <NumberInput source="year" />
 *                 <TextInput source="author" />
 *                 <TextInput source="country" />
 *                 <TextInput source="genre" />
 *                 <NumberInput source="pages" />
 *             </ReferenceOneInput>
 *         </SimpleForm>
 *     </Edit>
 * );
 */
export var ReferenceOneInput = function (_a) {
    var children = _a.children, className = _a.className, fullWidth = _a.fullWidth, label = _a.label, sx = _a.sx, props = __rest(_a, ["children", "className", "fullWidth", "label", "sx"]);
    var resource = useResourceContext(props);
    var _b = useReferenceOneInputController(props), inputKey = _b.inputKey, record = _b.record, fetchError = _b.error, isLoading = _b.isLoading, temporaryFieldName = _b.temporaryFieldName;
    var translate = useTranslate();
    var sourceContext = React.useMemo(function () { return ({
        getSource: function (source) {
            return source ? "".concat(temporaryFieldName, ".").concat(source) : temporaryFieldName;
        },
        getLabel: function (source) {
            return "resources.".concat(props.reference, ".fields.").concat(source);
        },
    }); }, [temporaryFieldName, props.reference]);
    if (isLoading) {
        return (_jsx(Root, { fullWidth: fullWidth, sx: sx, "data-testid": "loading", children: _jsx(Labeled, { label: label, source: props.reference, resource: resource, children: _jsx(LinearProgress, {}) }) }));
    }
    if (fetchError) {
        return (_jsx(Root, { fullWidth: fullWidth, sx: sx, "data-testid": "error", children: _jsx(Labeled, { label: label, source: props.reference, resource: resource, children: _jsx(Typography, { sx: { color: 'error.main' }, children: translate('ra-relationships.referenceOneInput.fetchError', {
                        _: 'Error fetching referenced record',
                    }) }) }) }));
    }
    return (_jsx(ResourceContextProvider, { value: props.reference, children: _jsx(Root, { fullWidth: fullWidth, className: clsx('ra-input', "ra-input-".concat(temporaryFieldName), className), sx: sx, children: _jsx(Stack, __assign({ className: ReferenceOneInputClasses.container }, sanitizeRestProps(props), { children: _jsx(RecordContextProvider, { value: record, children: _jsx(SourceContextProvider, { value: sourceContext, children: children }) }) })) }) }, inputKey));
};
var PREFIX = 'RaReferenceOneInput';
export var ReferenceOneInputClasses = {
    container: "".concat(PREFIX, "-container"),
};
var Root = styled(FormControl, {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function () { return ({
    marginTop: 0,
    marginBottom: 0,
}); });
var sanitizeRestProps = function (_a) {
    var sort = _a.sort, filter = _a.filter, reference = _a.reference, target = _a.target, source = _a.source, queryOptions = _a.queryOptions, mutationOptions = _a.mutationOptions, onBlur = _a.onBlur, onChange = _a.onChange, defaultValue = _a.defaultValue, record = _a.record, resource = _a.resource, shouldUnregister = _a.shouldUnregister, props = __rest(_a, ["sort", "filter", "reference", "target", "source", "queryOptions", "mutationOptions", "onBlur", "onChange", "defaultValue", "record", "resource", "shouldUnregister"]);
    return props;
};
